/**
 * 커버리지 × 가동률 산점도 — "좋아 보이는 것"과 "믿을 수 있는 것"은 다르다.
 *
 * 순위표만 보면 커버리지 1.5% 로 가동률 100% 를 찍은 대상이 1위가 된다.
 * 두 축을 함께 놓으면 그 점이 화면 **왼쪽 끝**에 홀로 떨어져 앉는다 —
 * 설명하지 않아도 보인다. 임계선 왼쪽은 순위에서 분리된 영역이다.
 *
 * 순위에 든 대상과 분리된 대상을 **같은 평면에 함께** 그린다. 분리된 것을
 * 안 그리면 "왜 얘가 없지?" 라는 질문에 답할 수 없다 (§7.2).
 */

import { useQuery } from '@tanstack/react-query'
import type { EChartsOption } from 'echarts'
import { useMemo } from 'react'

import { ChartFrame } from '@/components/ChartFrame'
import { EChart } from '@/components/EChart'
import { fetchEnvelope } from '@/lib/api'
import { axisStyle, niceBand } from '@/lib/chart'
import { cssVar } from '@/lib/cssvar'
import { int, markSize, minutes, pct, pctAxis } from '@/lib/fmt'
import { useEntityLabel } from '@/lib/labels'
import {
  RANK_ENTITIES,
  VERDICT_LABEL,
  type Envelope,
  type RankEntity,
  type RankingData,
  type RankingItem,
} from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'

/** 세로축 바닥. 이 아래 점은 그리지 않고 건수·이름을 각주로 밝힌다 */
const Y_FLOOR = 0.8

export function CoverageScatter({ entity }: { entity: RankEntity }) {
  const filters = useFilters()
  const labelOf = useEntityLabel()

  const q = useQuery({
    queryKey: ['metrics/ranking', entity, filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<RankingData>('/metrics/ranking', toQueryFilters(filters), { entity }, signal),
    staleTime: 5 * 60 * 1000,
  })

  const env = q.data as Envelope<RankingData> | undefined
  const included = useMemo(
    () => (env?.data.ranking ?? []).filter((r) => r.availability != null),
    [env],
  )
  const excluded = useMemo(
    () => (env?.data.excluded ?? []).filter((r) => r.availability != null),
    [env],
  )
  const all = useMemo(
    () => [...included, ...excluded].sort((a, b) => b.n_chargers - a.n_chargers),
    [included, excluded],
  )
  // 바닥의 이상치 한둘이 세로축 전체를 늘려 나머지를 위쪽 20% 에 몰아넣는다.
  // 축을 자르되 **몇 건이 잘렸는지 이름과 함께** 밝힌다 — 조용히 버리지 않는다
  const axisLabel = RANK_ENTITIES.find((e) => e.key === entity)?.label ?? '대상'
  const below = useMemo(
    () => all.filter((r) => (r.availability as number) < Y_FLOOR),
    [all],
  )

  const option = useMemo<EChartsOption>(() => {
    const accent = cssVar('--c-accent', '#0066cc')
    const na = cssVar('--c-na', '#6f6f75')
    const ink3 = cssVar('--c-ink-3', '#6e6e73')
    const hairline = cssVar('--c-hairline', '#e0e0e0')
    const canvas = cssVar('--c-canvas', '#ffffff')

    const ys = all.map((r) => r.availability as number).filter((v) => v >= Y_FLOOR)
    const lo = ys.length ? Math.min(...ys) : Y_FLOOR
    const hi = ys.length ? Math.max(...ys) : 1
    const pad = Math.max((hi - lo) * 0.1, 0.002)
    const band = niceBand(Math.max(0, lo - pad), Math.min(1, hi + pad))

    const point = (r: RankingItem) => ({
      value: [r.coverage, r.availability as number, r.n_chargers],
      name: labelOf(entity, r.entity_id),
    })

    return {
      grid: { left: 56, right: 24, top: 26, bottom: 44 },
      tooltip: {
        trigger: 'item',
        formatter: (p) => {
          const d = p as { name: string; value: number[] }
          return [
            `<b>${d.name}</b>`,
            `커버리지 ${pct(d.value[0], 1)}`,
            `가동률 ${pct(d.value[1])}`,
            `충전기 ${int(d.value[2])}기`,
          ].join('<br/>')
        },
      },
      xAxis: {
        type: 'value',
        min: 0,
        max: 1,
        name: '커버리지 — 관측된 시간의 비율',
        nameLocation: 'middle',
        nameGap: 26,
        nameTextStyle: { fontSize: 10, color: ink3 },
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3, formatter: pctAxis(1) },
      },
      yAxis: {
        type: 'value',
        min: band.min,
        max: band.max,
        interval: band.interval,
        name: '가동률',
        nameTextStyle: { fontSize: 10, color: ink3, align: 'left' },
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3, formatter: pctAxis(band.interval * 5) },
      },
      series: [
        {
          name: '순위 포함',
          type: 'scatter',
          data: included.map(point),
          symbolSize: (v: number[]) => markSize(v[2] ?? 1, 8, 38),
          itemStyle: { color: accent, opacity: 0.72, borderColor: canvas, borderWidth: 2 },
          markLine: {
            silent: true,
            symbol: 'none',
            lineStyle: { color: ink3, type: 'solid', width: 1 },
            // 라벨은 범례가 맡는다. 플롯 안에 두면 점 무리 또는 축 눈금과 겹친다
            label: { show: false },
            data: [{ xAxis: filters.minCoverage }],
          },
        },
        {
          name: '순위에서 분리',
          type: 'scatter',
          data: excluded.map(point),
          symbolSize: (v: number[]) => markSize(v[2] ?? 1, 8, 38),
          // 빈 마크 = "값은 있는데 근거가 얇다". 색 하나로만 구분하지 않는다
          itemStyle: { color: 'transparent', borderColor: na, borderWidth: 1.5, opacity: 0.85 },
        },
      ],
    }
  }, [all, included, excluded, entity, labelOf, filters.minCoverage])

  const table = (
    <div className="ctable">
      <table className="data-table">
        <caption className="sr-only">커버리지와 가동률</caption>
        <thead>
          <tr>
            <th scope="col">대상</th>
            <th scope="col" className="ta-r">
              충전기
            </th>
            <th scope="col" className="ta-r">
              커버리지
            </th>
            <th scope="col" className="ta-r">
              가동률
            </th>
            <th scope="col" className="ta-r">
              관측량
            </th>
            <th scope="col">순위 반영</th>
          </tr>
        </thead>
        <tbody>
          {all.map((r) => (
            <tr key={r.entity_id}>
              <td>{labelOf(entity, r.entity_id)}</td>
              <td className="num ta-r">{int(r.n_chargers)}</td>
              <td className="num ta-r">{pct(r.coverage, 1)}</td>
              <td className="num ta-r">{pct(r.availability)}</td>
              <td className="num ta-r">{minutes(r.t_obs_min)}</td>
              <td>{r.verdict ? VERDICT_LABEL[r.verdict] : included.includes(r) ? '반영' : '분리'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <ChartFrame
      title="커버리지 × 가동률"
      question="근거의 두께와 결과를 한 평면에 놓는다 — 왼쪽 위의 점은 '좋아 보이지만 안 본 시간이 대부분'이다"
      meta={env?.meta}
      reading={`${axisLabel} 하나가 점 하나입니다. 오른쪽으로 갈수록 관측이 두껍고, 위로 갈수록 가동률이 높습니다. 왼쪽 위에 홀로 있는 점은 "거의 못 봤는데 본 동안은 좋았다"는 뜻이라 믿을 근거가 얇습니다.`}
      encoding={[
        {
          role: '가로축',
          what: '커버리지 — 관측된 시간의 비율 (0~100%)',
          detail:
            '전체 시간 중 UP/DOWN 으로 판정할 수 있었던 비율입니다. 나머지는 사업자 연계 중단(STALE) 등으로 상태를 모르는 시간입니다. 근거의 두께는 절대량으로 봐야 하므로 이 축만은 0~100% 전체를 보여줍니다.',
        },
        {
          role: '세로축',
          what: `가동률 (${pct(Y_FLOOR, 0)} 이상 구간만)`,
          detail:
            '값이 위쪽에 몰려 있어 바닥까지 그리면 관심 구간이 화면 위 20%로 눌립니다. 잘린 대상은 아래 주의사항에 이름과 값을 적었습니다.',
        },
        {
          role: '원 크기',
          what: '충전기 수 (지름 ∝ √n)',
          detail: '큰 원 하나가 작은 원 여럿보다 전체 수치에 크게 기여합니다.',
        },
        {
          role: '채운 원',
          what: '순위에 반영된 대상',
          detail: '커버리지 기준을 넘고 비교가능 판정을 받은 대상입니다.',
        },
        {
          role: '빈 원',
          what: '순위에서 분리된 대상',
          detail: '값은 있지만 근거가 얇거나 비교 조건을 통과하지 못했습니다. 지우지 않고 함께 그립니다.',
        },
        {
          role: '세로선',
          what: `커버리지 기준 ${pct(filters.minCoverage, 0)}`,
          detail: '이 선의 왼쪽은 기준 미달 영역입니다. 기준값은 상단 필터의 "표시 조건"에서 바꿀 수 있습니다.',
        },
      ]}
      legend={[
        {
          label: `순위 반영 ${included.length}건`,
          color: cssVar('--c-accent', '#0066cc'),
          pattern: 'dot',
        },
        {
          label: `순위에서 분리 ${excluded.length}건`,
          color: cssVar('--c-na', '#6f6f75'),
          pattern: 'ring',
        },
        {
          label: `세로선 = 커버리지 기준 ${pct(filters.minCoverage, 0)}`,
          color: cssVar('--c-ink-3', '#6e6e73'),
          pattern: 'line',
        },
      ]}
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={all.length === 0}
      onRetry={() => void q.refetch()}
      table={table}
      notes={[
        {
          label: '좋아 보이는 것과 믿을 수 있는 것은 다르다.',
          text: '순위표만 보면 커버리지 1.5%로 가동률 100%를 찍은 대상이 1위가 됩니다. 두 축을 함께 놓으면 그 점이 왼쪽 끝에 홀로 떨어져 앉아 설명 없이도 보입니다.',
        },
        {
          label: '분리된 대상을 숨기지 않는다.',
          text: '지우면 "왜 이 대상이 순위에 없는가"에 답할 수 없습니다. 사유는 표 보기에 있습니다.',
        },
        below.length > 0
          ? {
              label: `세로축 밖 ${below.length}건.`,
              text: `가동률 ${pct(Y_FLOOR, 0)} 미만이라 점으로 그리지 않았습니다: ${below
                .map((r) => `${labelOf(entity, r.entity_id)} ${pct(r.availability, 1)}`)
                .join(', ')}. 표 보기에는 모두 들어 있습니다.`,
            }
          : '',
      ]}

    >
      <EChart
        option={option}
        height={320}
        ariaLabel={`커버리지 대 가동률 산점도. 순위 반영 ${included.length}건, 분리 ${excluded.length}건. 정확한 값은 표 보기에 있습니다.`}
      />
    </ChartFrame>
  )
}
